import React from 'react';
import { motion } from 'framer-motion';
import { Award, Camera, Heart, Users } from 'lucide-react';

const About = () => {
  const stats = [
    { icon: Camera, value: '500+', label: 'Events Covered' },
    { icon: Users, value: '1000+', label: 'Happy Clients' },
    { icon: Award, value: '15+', label: 'Awards Won' },
    { icon: Heart, value: '10+', label: 'Years Experience' },
  ];

  return (
    <section id="about" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image Column */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/images/photo-21.jpg"
                alt="Nik - Professional Photographer"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/80 via-transparent to-transparent" />
            </div>
            
            {/* Experience Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="absolute -bottom-8 -right-8 md:bottom-8 md:right-8 bg-[#d4af37] p-6 md:p-8"
            >
              <p className="text-[#0a0a0a] text-4xl md:text-5xl font-['Playfair_Display'] font-medium">10+</p>
              <p className="text-[#0a0a0a]/80 text-xs tracking-widest uppercase mt-1">Years of Excellence</p>
            </motion.div>

            {/* Decorative Border */}
            <div className="absolute -top-4 -left-4 w-full h-full border border-[#d4af37]/20 -z-10" />
          </motion.div>

          {/* Content Column */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light">
                About the Artist
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5] leading-tight">
                Crafting Visual
                <br />
                <span className="italic">Masterpieces</span>
              </h2>
            </div>

            <div className="space-y-6 text-[#a3a3a3] leading-relaxed">
              <p className="text-lg">
                Welcome to <span className="text-[#d4af37]">NIK'S Photography & Studio</span>, 
                where every frame is a work of art. With over a decade of experience, 
                I've had the privilege of capturing life's most precious moments for 
                countless families and couples.
              </p>
              <p>
                My approach combines technical excellence with artistic vision, 
                creating images that not only document your special moments but 
                transform them into timeless treasures. From intimate portraits to 
                grand wedding celebrations, I bring the same passion and dedication 
                to every shoot.
              </p>
              <p>
                Based in India, I specialize in wedding photography, portraits, 
                corporate events, and product photography. My goal is simple: 
                to exceed your expectations and deliver images that you'll cherish 
                for generations.
              </p>
            </div>

            {/* Signature */}
            <div className="pt-4">
              <p className="font-['Cormorant_Garamond'] text-3xl italic text-[#d4af37]">
                Nikhil Kumar
              </p>
              <p className="text-[#a3a3a3] text-sm tracking-wider mt-1">
                Founder & Lead Photographer
              </p>
            </div>
          </motion.div>
        </div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 pt-16 border-t border-[#262626]"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center group"
            >
              <stat.icon className="w-8 h-8 mx-auto text-[#d4af37] mb-4 group-hover:scale-110 transition-transform" />
              <p className="text-3xl md:text-4xl font-['Playfair_Display'] text-[#e5e5e5] mb-1">
                {stat.value}
              </p>
              <p className="text-[#a3a3a3] text-xs tracking-widest uppercase">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
